import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, BookOpen, Compass, ArrowRight, Heart, Shield, LifeBuoy } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import { getSakhiPhoto } from "../lib/kioskConfig";

const DEFAULT_PHOTO = "https://images.unsplash.com/photo-1706943262459-3ef6ce03305c?crop=entropy&cs=srgb&fm=jpg&w=900&h=1125&fit=crop&q=85";

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(() => getSakhiPhoto() || DEFAULT_PHOTO);

  useEffect(() => {
    const refresh = () => setPhoto(getSakhiPhoto() || DEFAULT_PHOTO);
    window.addEventListener("sakhi:photo-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("sakhi:photo-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const tiles = [
    {
      key: "chat",
      testid: "tile-chat",
      title: t("chat_cta"),
      desc: t("chat_intro"),
      icon: <MessageCircle className="w-9 h-9" strokeWidth={2.5} />,
      to: "/chat",
      accent: "bg-primary text-white",
      badge: <Heart className="w-4 h-4" />,
    },
    {
      key: "edu",
      testid: "tile-education",
      title: t("education_hub"),
      desc: t("education_desc"),
      icon: <BookOpen className="w-9 h-9" strokeWidth={2.5} />,
      to: "/education",
      accent: "bg-accent text-white",
      badge: <Shield className="w-4 h-4" />,
    },
    {
      key: "career",
      testid: "tile-career",
      title: t("career_hub"),
      desc: t("career_desc"),
      icon: <Compass className="w-9 h-9" strokeWidth={2.5} />,
      to: "/career",
      accent: "bg-secondary text-foreground",
      badge: <Compass className="w-4 h-4" />,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-16">
      {/* Hero */}
      <section className="grid lg:grid-cols-5 gap-8 lg:gap-10 items-center fade-up">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-border px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-primary mb-4 sm:mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            {t("tagline")}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground">
            {t("hero_title")}
          </h1>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
            {t("hero_sub")}
          </p>
          <div className="mt-6 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              data-testid="hero-chat-btn"
              onClick={() => navigate("/chat")}
              className="pill-btn pill-btn-primary text-base sm:text-lg gap-2 h-auto"
            >
              <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
              {t("chat_cta")}
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button
              data-testid="hero-sos-btn"
              onClick={() => window.dispatchEvent(new Event("sakhi:open-sos"))}
              className="pill-btn text-base sm:text-lg gap-2 h-auto bg-destructive text-white hover:brightness-110 shadow-[0_10px_28px_rgba(180,40,40,0.28)]"
            >
              <LifeBuoy className="w-5 h-5 sm:w-6 sm:h-6" />
              {t("get_help_now")}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(30,63,45,0.15)] aspect-[4/5] max-w-sm mx-auto lg:max-w-none bg-secondary/30">
            <img
              src={photo}
              alt="Sakhi Didi — a calm mid-20s mentor"
              className="w-full h-full object-cover"
              data-testid="hero-photo"
            />
            <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 bg-white/95 backdrop-blur rounded-2xl p-3 sm:p-4 border border-border">
              <div className="text-sm font-bold text-primary">Sakhi Didi</div>
              <div className="text-xs sm:text-sm text-foreground/80 mt-1">
                {t("chat_intro")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tiles */}
      <section className="mt-12 sm:mt-20 grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
        {tiles.map((tile, idx) => (
          <button
            key={tile.key}
            data-testid={tile.testid}
            onClick={() => navigate(tile.to)}
            className="text-left group card-lift bg-white rounded-3xl p-5 sm:p-8 border border-border relative overflow-hidden fade-up"
            style={{ animationDelay: `${idx * 80}ms` }}
          >
            <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl ${tile.accent} flex items-center justify-center mb-3 sm:mb-5 shadow-md`}>
              {tile.icon}
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-1 sm:mb-2">{tile.title}</h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{tile.desc}</p>
            <div className="mt-4 sm:mt-6 inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
              <span>Open</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </button>
        ))}
      </section>
    </div>
  );
}
